import { useNavigate } from "react-router-dom";
import { useTheme } from "../../Context/themeContext";
import { Facebook, Twitter, Linkedin, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();

    function handleClick(navigation) {
        if (navigation === 'Term-Privacy') navigate('/term-privacy');
        if (navigation === 'Products') navigate('/product');
        if (navigation === 'Contact') navigate('/contact');
        if (navigation === 'Home') navigate('/');
        if (navigation === 'About') navigate('/about');
        if (navigation === 'Help') navigate('/help');
    }

    // Theme-aware classes
    const borderColor = theme === 'dark' 
        ? 'border-blue-800/40' 
        : 'border-gray-200';
    
    const bgColor = theme === 'dark'
        ? 'bg-[#0B0F1C]/80'
        : 'bg-white/80';

    const textColor = theme === 'dark'
        ? 'text-blue-200/60'
        : 'text-gray-500';
    
    const headingColor = theme === 'dark'
        ? 'text-white'
        : 'text-gray-900';

    const linkColor = theme === 'dark'
        ? 'text-blue-300 hover:text-white'
        : 'text-gray-600 hover:text-gray-900';

    const iconBg = theme === 'dark'
        ? 'bg-white/5 hover:bg-white/10'
        : 'bg-gray-100 hover:bg-gray-200';

    const iconColor = theme === 'dark'
        ? 'text-blue-400'
        : 'text-blue-600';

    const footerLinks = [
        { label: 'Home', key: 'Home' },
        { label: 'About', key: 'About' },
        { label: 'Products', key: 'Products' },
        { label: 'Contact', key: 'Contact' },
        { label: 'Help', key: 'Help' },
        { label: 'Terms & Privacy', key: 'Term-Privacy' },
    ];

    const socialLinks = [
        { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61579466615993', label: 'Facebook' },
        { icon: Twitter, href: 'https://x.com/BandyMoot', label: 'Twitter' },
        { icon: Linkedin, href: 'https://www.linkedin.com/company/bandy-moot-private-limited', label: 'LinkedIn' },
        { icon: Mail, href: 'mailto:sales@bandy-moot.com', label: 'Email' },
    ];

    return (
        <footer className={`${bgColor} backdrop-blur-xl transition-colors duration-300`}>
            {/* Main Footer Content */}
            <div className="px-4 pt-6 pb-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
                    {/* Company Info */}
                    <div className="col-span-1 md:col-span-2">
                        <h3 className={`text-xl font-bold ${headingColor} mb-4`}>
                            Bandy & Moot
                        </h3>
                        <p className={`${textColor} text-sm leading-relaxed max-w-md`}>
                            Empowering businesses with innovative time and attendance solutions. 
                            We help organizations streamline their workforce management with 
                            cutting-edge technology.
                        </p>
                        <div className="flex items-center gap-4 mt-4">
                            <div className={`flex items-center gap-2 ${textColor} text-sm`}>
                                <Phone className="w-4 h-4" />
                                <span>+91 7905226299</span>
                            </div>
                            <div className={`flex items-center gap-2 ${textColor} text-sm`}>
                                <MapPin className="w-4 h-4" />
                                <span>Prayagraj, India</span>
                            </div>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className={`text-sm font-semibold ${headingColor} uppercase tracking-wider mb-4`}>
                            Quick Links
                        </h4>
                        <ul className="space-y-1">
                            {footerLinks.slice(0, 4).map(({ label, key }) => (
                                <li key={key}>
                                    <button
                                        onClick={() => handleClick(key)}
                                        className={`${linkColor} transition-all duration-300 text-sm hover:translate-x-1 inline-block`}
                                    >
                                        {label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Legal & Social */}
                    <div>
                        <h4 className={`text-sm font-semibold ${headingColor} uppercase tracking-wider mb-4`}>
                            Legal
                        </h4>
                        <ul className="space-y-1">
                            {footerLinks.slice(4).map(({ label, key }) => (
                                <li key={key}>
                                    <button
                                        onClick={() => handleClick(key)}
                                        className={`${linkColor} transition-all duration-300 text-sm hover:translate-x-1 inline-block`}
                                    >
                                        {label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        
                        {/* Social Icons */}
                        <div className="mt-3">
                            <h4 className={`text-sm font-semibold ${headingColor} uppercase tracking-wider mb-3`}>
                                Follow Us
                            </h4>
                            <div className="flex items-center gap-3">
                                {socialLinks.map(({ icon: Icon, href, label }) => (
                                    <a
                                        key={label}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`${iconBg} p-2 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg`}
                                        aria-label={label}
                                    >
                                        <Icon className={`w-4 h-4 ${iconColor}`} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className={`border-t ${borderColor} mt-4 pt-3 transition-colors duration-300`}>
                    <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                       <p className={`${textColor} text-sm transition-colors duration-300 flex items-center gap-1`}>
                            © 2026 Bandy & Moot Pvt Ltd. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
